import React, { useEffect, useMemo, useState } from "react"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import {
  calculatedMetricCatalogEntries,
  getMetricCatalogEntry,
  type CalculatedMetricDef,
  type CustomReportGroup,
  type GroupVisualization,
  type MetricCatalogEntry,
} from "~/shared/kpi"
import Input from "~/ui-rtk/components/ui/controls/Input"
import SegmentedToggle from "~/ui-rtk/components/ui/controls/SegmentedToggle/SegmentedToggle"
import SortableMetricRow from "./SortableMetricRow"
import MetricCatalogPicker from "./MetricCatalogPicker"

interface GroupEditorModalProps {
  isOpen: boolean
  /** The custom group being edited. */
  group: CustomReportGroup
  /** Report-scoped calculated metrics, surfaced in the picker as their own category. */
  calculatedMetrics: CalculatedMetricDef[]
  /** Company custom metrics, surfaced in the picker under the Custom Metrics category. */
  customEntries?: MetricCatalogEntry[]
  /**
   * Synthesized CUSTOM_* initiative-type breakout entries, surfaced in the
   * picker under the Initiative Type Breakouts category (alongside the static
   * predefined-type entries from the frozen catalog).
   */
  initiativeEntries?: MetricCatalogEntry[]
  onClose: () => void
  onSave: (group: CustomReportGroup) => void
}

// Edits a single custom group: title, optional totals row, the ordered list of
// metric rows (reorder/remove + per-metric period-over-period growth toggle),
// and a catalog picker to add more. Works on a local copy; `onSave` hands the
// updated group back to the parent editor's working config.
const GroupEditorModal: React.FC<GroupEditorModalProps> = ({
  isOpen,
  group,
  calculatedMetrics,
  customEntries,
  initiativeEntries,
  onClose,
  onSave,
}) => {
  const calcEntries = useMemo(
    () => calculatedMetricCatalogEntries(calculatedMetrics),
    [calculatedMetrics],
  )
  // Report-scoped calc metrics + company-scoped custom metrics and custom
  // initiative-type breakouts all ride the picker's extraEntries and the
  // row-label resolver (none is in the frozen global catalog).
  const extraEntries = useMemo(
    () => [
      ...calcEntries,
      ...(customEntries ?? []),
      ...(initiativeEntries ?? []),
    ],
    [calcEntries, customEntries, initiativeEntries],
  )
  const extraEntryByKey = useMemo(
    () => new Map(extraEntries.map(entry => [entry.key, entry])),
    [extraEntries],
  )
  const resolveEntry = (key: string): MetricCatalogEntry | undefined =>
    extraEntryByKey.get(key) ?? getMetricCatalogEntry(key)

  const [title, setTitle] = useState(group.title)
  const [metricKeys, setMetricKeys] = useState<string[]>(group.metricKeys)
  const [wowGrowthKeys, setWowGrowthKeys] = useState<string[]>(
    group.wowGrowthMetricKeys ?? [],
  )
  const [showTotals, setShowTotals] = useState(group.showTotals ?? false)
  const [visualization, setVisualization] = useState<GroupVisualization>(
    group.visualization ?? "table",
  )

  // Reseed local state whenever a different group is opened.
  useEffect(() => {
    setTitle(group.title)
    setMetricKeys(group.metricKeys)
    setWowGrowthKeys(group.wowGrowthMetricKeys ?? [])
    setShowTotals(group.showTotals ?? false)
    setVisualization(group.visualization ?? "table")
  }, [group])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setMetricKeys(keys => {
        const oldIndex = keys.indexOf(active.id as string)
        const newIndex = keys.indexOf(over.id as string)
        return arrayMove(keys, oldIndex, newIndex)
      })
    }
  }

  const handleAddMetric = (key: string) => {
    setMetricKeys(keys => (keys.includes(key) ? keys : [...keys, key]))
  }

  const handleRemoveMetric = (key: string) => {
    setMetricKeys(keys => keys.filter(k => k !== key))
    setWowGrowthKeys(keys => keys.filter(k => k !== key))
  }

  const handleToggleWowGrowth = (key: string) => {
    setWowGrowthKeys(keys =>
      keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key],
    )
  }

  const handleSave = () => {
    onSave({
      kind: "custom",
      id: group.id,
      title: title.trim() || "Untitled Group",
      metricKeys,
      ...(wowGrowthKeys.length > 0
        ? {
            wowGrowthMetricKeys: wowGrowthKeys.filter(k =>
              metricKeys.includes(k),
            ),
          }
        : {}),
      showTotals,
      // Absent = table, so only a chart choice is persisted on the group.
      ...(visualization !== "table" ? { visualization } : {}),
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-system-blue-900 border border-system-blue-700 rounded-system-lg shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
        <div className="p-6 flex items-center justify-between border-b border-system-blue-700">
          <h2 className="text-xl font-semibold text-white">Edit Group</h2>
          <button
            onClick={onClose}
            className="text-system-blue-300 hover:text-white transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 flex flex-col gap-4 overflow-hidden">
          <div className="flex items-center gap-4">
            <Input
              fullWidth
              label="Group title"
              value={title}
              onChange={event => setTitle(event.target.value)}
              classes={{ input: "text-white" }}
            />
            <label className="flex items-center gap-2 text-sm text-system-blue-200 cursor-pointer select-none whitespace-nowrap">
              <input
                type="checkbox"
                checked={showTotals}
                onChange={() => setShowTotals(value => !value)}
                className="checkbox checkbox-sm"
              />
              Show totals row
            </label>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-system-blue-200 whitespace-nowrap">
              Display as
            </span>
            <SegmentedToggle
              variant="joined"
              options={[
                { label: "Table", value: "table" },
                { label: "Line chart", value: "line" },
                { label: "Bar chart", value: "bar" },
              ]}
              value={visualization}
              onChange={value => setVisualization(value as GroupVisualization)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 overflow-hidden">
            <div className="flex flex-col overflow-hidden">
              <p className="text-white text-sm font-semibold mb-2">
                Metrics in this group ({metricKeys.length})
              </p>
              <div className="flex-1 overflow-y-auto pr-1 min-h-[20rem] max-h-[28rem]">
                {metricKeys.length === 0 ? (
                  <p className="text-system-blue-300 text-sm">
                    No metrics yet — add some from the catalog.
                  </p>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={metricKeys}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {metricKeys.map(key => {
                          const entry = resolveEntry(key)
                          return (
                            <SortableMetricRow
                              key={key}
                              id={key}
                              label={entry?.label ?? key}
                              category={entry?.category ?? "Unknown"}
                              isWowGrowth={wowGrowthKeys.includes(key)}
                              onToggleWowGrowth={() =>
                                handleToggleWowGrowth(key)
                              }
                              onRemove={() => handleRemoveMetric(key)}
                            />
                          )
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>

            <div className="flex flex-col overflow-hidden">
              <p className="text-white text-sm font-semibold mb-2">
                Add metrics
              </p>
              <div className="flex-1 overflow-hidden min-h-[20rem] max-h-[28rem]">
                <MetricCatalogPicker
                  selectedKeys={metricKeys}
                  extraEntries={extraEntries}
                  onAdd={handleAddMetric}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 flex items-center justify-end gap-3 border-t border-system-blue-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-system-blue-300 hover:text-white border border-system-blue-600 hover:border-system-blue-500 rounded-system-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm text-white bg-system-brand-pink-500 hover:bg-system-brand-pink-600 rounded-system-md transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

export default GroupEditorModal
